import React, { useCallback, useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Check } from 'lucide-react';
import { useCurrency } from '../hooks/useCurrency';
const IDLE_DELAY_MS = 90000; // 90 seconds before idle popup
const EXIT_INTENT_MIN_TIME_MS = 60000; // Exit intent only after 60s on page
const MAX_SHOWS = 1;
const DISCOUNT_CODE = 'EXTRA20';
const DISCOUNTED_PRICE = 14;
const ORIGINAL_PRICE = 96; // Base original price (guide + bonuses)
const PRODUCT_IMAGE = 'https://cdn.magicpatterns.com/uploads/9kfBDM9XGTP6kXkwGVeVWH/kndd_hero_thenap2.png';
function getShowCount(): number {
  try {
    return parseInt(sessionStorage.getItem('discount_show_count') || '0', 10);
  } catch {
    return 0;
  }
}
function setStoredCount(n: number) {
  sessionStorage.setItem('discount_show_count', String(n));
}
export function DiscountPopup() {
  const [isOpen, setIsOpen] = useState(false);
  const [showCount, setShowCount] = useState(getShowCount);
  const [bump1, setBump1] = useState(false);
  const [bump2, setBump2] = useState(false);
  const {
    formatPrice
  } = useCurrency();
  const secondChanceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMaxed = showCount >= MAX_SHOWS;
  const isSecondShow = showCount >= 1;
  const currentTotal = DISCOUNTED_PRICE + (bump1 ? 9 : 0) + (bump2 ? 9 : 0);
  const currentOriginal = ORIGINAL_PRICE + (bump1 ? 14 : 0) + (bump2 ? 14 : 0);
  const showPopup = useCallback(() => {
    setShowCount(prev => {
      if (prev >= MAX_SHOWS) return prev;
      const next = prev + 1;
      setStoredCount(next);
      return next;
    });
    setIsOpen(true);
  }, []);
  // Guard: should we listen for triggers?
  const shouldListen = !isMaxed && !isOpen;
  const cancelAll = useCallback(() => {
    setStoredCount(MAX_SHOWS);
    setShowCount(MAX_SHOWS);
    setIsOpen(false);
    if (secondChanceTimer.current) clearTimeout(secondChanceTimer.current);
  }, []);
  // ─── Trigger 1: General idle timer (45s fallback) ───
  useEffect(() => {
    if (!shouldListen) return;
    const timer = setTimeout(showPopup, IDLE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [shouldListen, showPopup]);
  // ─── Trigger 2: Exit intent — mouse leaves viewport (desktop, after min time) ───
  const pageLoadTime = useRef(Date.now());
  useEffect(() => {
    if (!shouldListen) return;
    const handleMouseLeave = (e: MouseEvent) => {
      const timeOnPage = Date.now() - pageLoadTime.current;
      if (e.clientY <= 0 && timeOnPage >= EXIT_INTENT_MIN_TIME_MS) showPopup();
    };
    document.addEventListener('mouseleave', handleMouseLeave);
    return () => document.removeEventListener('mouseleave', handleMouseLeave);
  }, [shouldListen, showPopup]);
  // ─── Cancel permanently if user clicks any CTA ───
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('[data-cta]') || target.closest('a[href*="gemmamama"]') || target.closest('button[class*="bg-sage"]') || target.closest('button[class*="bg-terracotta"]')) {
        cancelAll();
      }
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [cancelAll]);
  // ─── Lock scroll when open ───
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);
  const handleClose = () => {
    setIsOpen(false);
    // If we haven't hit max, schedule a second chance
    if (showCount < MAX_SHOWS) {
      secondChanceTimer.current = setTimeout(() => {
        showPopup();
      }, SECOND_CHANCE_DELAY_MS);
    }
  };
  const getCheckoutUrl = () => {
    let baseUrl = 'https://www.gemmamama.com/cart/45389317111947:1';
    if (bump1 && bump2) {
      baseUrl = 'https://www.gemmamama.com/cart/46426343342219:1';
    } else if (bump1) {
      baseUrl = 'https://www.gemmamama.com/cart/46426337214603:1';
    } else if (bump2) {
      baseUrl = 'https://www.gemmamama.com/cart/46426346487947:1';
    }
    return `${baseUrl}?discount=${DISCOUNT_CODE}`;
  };
  const handleClaim = () => {
    if (typeof window !== 'undefined' && (window as any).fbq) {
      ;
      (window as any).fbq('track', 'InitiateCheckout', {
        content_name: 'Soft Hold — Extra 20% Off',
        value: currentTotal,
        currency: 'USD'
      });
    }
    window.open(getCheckoutUrl(), '_blank');
    cancelAll();
  };
  // Cleanup second chance timer on unmount
  useEffect(() => {
    return () => {
      if (secondChanceTimer.current) clearTimeout(secondChanceTimer.current);
    };
  }, []);
  return <AnimatePresence data-id="element-89">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.35
      }} onClick={handleClose} className="fixed inset-0 bg-brown/30 backdrop-blur-sm z-[60]" data-id="element-90" />

          {/* Modal */}
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-5 pointer-events-none" data-id="element-91">
            <motion.div initial={{
          opacity: 0,
          scale: 0.96,
          y: 16
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.96,
          y: 16
        }} transition={{
          duration: 0.45,
          ease: [0.25, 0.1, 0.25, 1]
        }} className="w-full max-w-[320px] overflow-hidden pointer-events-auto rounded-[20px] relative" style={{
          background: '#FCFAF6',
          boxShadow: '0 20px 60px rgba(61,50,41,0.10), 0 2px 8px rgba(61,50,41,0.04)'
        }} data-id="element-92">
              {/* Close button */}
              <button onClick={handleClose} className="absolute top-3 right-3 z-20 w-7 h-7 flex items-center justify-center rounded-full hover:bg-brown/[0.04] transition-colors duration-200 text-brown/30 hover:text-brown/60" aria-label="Close" data-id="element-93">
                <X className="w-3.5 h-3.5" strokeWidth={1.5} data-id="element-94" />
              </button>

              <div className="px-8 pt-9 pb-7 text-center" data-id="element-95">
                {/* Tiny botanical accent */}
                <div className="flex justify-center mb-5" data-id="element-96">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="text-sage/50" data-id="element-97">
                    <path d="M12 3 C 8 7, 8 12, 12 17 C 16 12, 16 7, 12 3 Z" stroke="currentColor" strokeWidth="0.8" strokeLinecap="round" data-id="element-98" />
                    <path d="M12 7 L 12 21" stroke="currentColor" strokeWidth="0.8" strokeLinecap="round" data-id="element-99" />
                  </svg>
                </div>

                {/* Eyebrow */}
                <p className="text-[9px] uppercase tracking-[0.28em] text-terracotta/70 font-medium mb-4" data-id="element-100">
                  Extra 20% off · Today only
                </p>

                {/* Headline */}
                <h2 className="font-serif text-[1.35rem] text-brown leading-[1.25] mb-3 font-normal" data-id="element-101">
                  Your peace shouldn't
                  <br data-id="element-102" />
                  <em className="italic font-light" data-id="element-103">have to wait.</em>
                </h2>

                {/* Body */}
                <p className="text-brown-light/60 text-[12.5px] leading-relaxed mb-6 max-w-[260px] mx-auto" data-id="element-104">
                  You are so close to a calmer postpartum. Grab Soft Hold now
                  for{' '}
                  <span className="text-terracotta/90 italic font-serif" data-id="element-105">
                    an extra 20% off
                  </span>{' '}
                  and start feeling like yourself again today.
                </p>

                {/* Price */}
                <div className="flex items-baseline justify-center gap-2.5 mb-1.5" data-id="element-106">
                  <motion.span key={currentOriginal} initial={{
                opacity: 0,
                y: -3
              }} animate={{
                opacity: 1,
                y: 0
              }} className="font-serif text-[13px] text-brown/25 line-through italic" data-id="element-107">
                    {formatPrice(currentOriginal)}
                  </motion.span>
                  <motion.span key={currentTotal} initial={{
                opacity: 0,
                scale: 0.96
              }} animate={{
                opacity: 1,
                scale: 1
              }} className="font-serif text-[1.75rem] text-terracotta tracking-tight leading-none font-normal" data-id="element-108">
                    {formatPrice(currentTotal)}
                  </motion.span>
                </div>

                <p className="text-brown/30 text-[9px] uppercase tracking-[0.22em] mb-6 font-light" data-id="element-109">
                  Guide + 3 bonuses · Yours forever
                </p>

                {/* Hairline divider */}
                <div className="h-px bg-brown/[0.06] mb-5 mx-auto max-w-[200px]" data-id="element-110" />

                {/* Order Bumps — softer, no box */}
                <div className="mb-6 text-left" data-id="element-111">
                  <p className="text-[9px] uppercase tracking-[0.22em] text-sage/80 font-medium mb-4 text-center" data-id="element-112">
                    Bundle & Save
                  </p>

                  {/* Bump 1 */}
                  <div className="flex items-start gap-3 mb-4 cursor-pointer group" onClick={() => setBump1(!bump1)} data-id="element-113">
                    <div className={`w-[14px] h-[14px] rounded-[3px] border shrink-0 mt-[3px] flex items-center justify-center transition-all duration-200 ${bump1 ? 'bg-sage border-sage' : 'border-brown/25 bg-transparent group-hover:border-sage/60'}`} data-id="element-114">
                      {bump1 && <Check className="w-2 h-2 text-white" strokeWidth={3.5} data-id="element-115" />}
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-116">
                      <div className="flex items-baseline justify-between gap-2" data-id="element-117">
                        <p className="font-serif text-[13px] text-brown leading-tight" data-id="element-118">
                          Emergency Calm Cards
                        </p>
                        <span className="font-serif text-[12px] text-sage/90 shrink-0 italic" data-id="element-119">
                          +{formatPrice(9)}
                        </span>
                      </div>
                      <p className="text-[10.5px] text-brown-light/55 leading-snug mt-1" data-id="element-120">
                        10 Calm Cards for instant in-the-moment resets
                      </p>
                    </div>
                  </div>

                  {/* Bump 2 */}
                  <div className="flex items-start gap-3 cursor-pointer group" onClick={() => setBump2(!bump2)} data-id="element-121">
                    <div className={`w-[14px] h-[14px] rounded-[3px] border shrink-0 mt-[3px] flex items-center justify-center transition-all duration-200 ${bump2 ? 'bg-sage border-sage' : 'border-brown/25 bg-transparent group-hover:border-sage/60'}`} data-id="element-122">
                      {bump2 && <Check className="w-2 h-2 text-white" strokeWidth={3.5} data-id="element-123" />}
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-124">
                      <div className="flex items-baseline justify-between gap-2" data-id="element-125">
                        <p className="font-serif text-[13px] text-brown leading-tight" data-id="element-126">
                          Sanctuary Shields
                        </p>
                        <span className="font-serif text-[12px] text-sage/90 shrink-0 italic" data-id="element-127">
                          +{formatPrice(9)}
                        </span>
                      </div>
                      <p className="text-[10.5px] text-brown-light/55 leading-snug mt-1" data-id="element-128">
                        Scripts to protect your peace from unsolicited advice
                      </p>
                    </div>
                  </div>
                </div>

                {/* CTA — softer, refined */}
                <motion.button onClick={handleClaim} whileHover={{
              scale: 1.01
            }} whileTap={{
              scale: 0.99
            }} className="w-full py-3.5 rounded-full font-serif text-[14px] text-white cursor-pointer transition-all duration-300 mb-3.5" style={{
              background: '#b07258',
              boxShadow: '0 2px 10px rgba(176,114,88,0.18)'
            }} data-id="element-129">
                  Claim my calm
                </motion.button>

                {/* Dismiss */}
                <button onClick={handleClose} className="text-[10.5px] text-brown/30 hover:text-brown/55 italic font-serif transition-colors duration-200" data-id="element-130">
                  No thanks, I'll figure it out on my own
                </button>
              </div>
            </motion.div>
          </div>
        </>}
    </AnimatePresence>;
}